import { useState, useEffect, useRef, useMemo } from "react";
import { Upload, Image, FileText, Film, Trash2, Copy, Check, X, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fileTypeIcons = { image: Image, video: Film, document: FileText };

// Media Upload Component
export const MediaUploader = ({ token, onSelect }) => {
  const [uploading, setUploading] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await axios.get(`${API}/admin/media`, { headers });
      setMediaFiles(res.data);
    } catch (e) {
      console.error("Error fetching media:", e);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Max 10MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/media/upload`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded!");
      fetchMedia();
      if (onSelect) onSelect(res.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(`${API}/admin/media/${mediaId}`, { headers });
      toast.success("File deleted");
      fetchMedia();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedId(url);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Upload Button */}
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,.pdf,.doc,.docx,.txt"
          onChange={handleUpload}
          className="hidden"
          data-testid="media-file-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="border-dashed border-2 border-[#D1CFC5] hover:border-[#2C4A3B]"
          data-testid="media-upload-btn"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          {uploading ? "Uploading..." : "Upload File"}
        </Button>
        <span className="text-xs text-[#5C5A52]">Max 10MB. Images, videos, PDFs.</span>
      </div>

      {/* Media Grid */}
      {mediaFiles.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-[300px] overflow-y-auto">
          {mediaFiles.map((file) => {
            const Icon = fileTypeIcons[file.file_type] || FileText;
            return (
              <div
                key={file.id}
                className="bg-[#F7F6F2] border border-[#D1CFC5] rounded-lg p-2 relative group"
              >
                {file.file_type === "image" ? (
                  <img
                    src={file.url}
                    alt={file.original_filename}
                    className="w-full h-24 object-cover rounded cursor-pointer"
                    onClick={() => onSelect && onSelect(file.url)}
                    data-testid={`media-thumb-${file.id}`}
                  />
                ) : (
                  <div
                    className="w-full h-24 flex items-center justify-center bg-[#EFEDE5] rounded cursor-pointer"
                    onClick={() => onSelect && onSelect(file.url)}
                  >
                    <Icon className="w-8 h-8 text-[#5C5A52]" />
                  </div>
                )}
                <p className="text-xs text-[#5C5A52] truncate mt-1">{file.original_filename}</p>
                <p className="text-xs text-[#5C5A52]/60">{formatSize(file.file_size)}</p>

                {/* Actions overlay */}
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => copyUrl(file.url)}
                    className="bg-white/90 p-1 rounded shadow-sm hover:bg-white"
                    title="Copy URL"
                  >
                    {copiedId === file.url ? (
                      <Check className="w-3 h-3 text-[#2C4A3B]" />
                    ) : (
                      <Copy className="w-3 h-3 text-[#5C5A52]" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="bg-white/90 p-1 rounded shadow-sm hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Blog Post Editor Modal
export const BlogPostEditor = ({ isOpen, onClose, post = null, token, onSaved }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [category, setCategory] = useState("news");
  const [author, setAuthor] = useState("ekvalleys");
  const [imageUrl, setImageUrl] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const isEdit = !!post;

  useEffect(() => {
    if (post) {
      setTitle(post.title || "");
      setContent(post.content || "");
      setExcerpt(post.excerpt || "");
      setCategory(post.category || "news");
      setAuthor(post.author || "ekvalleys");
      setImageUrl(post.image_url || "");
      setIsFeatured(post.is_featured || false);
    } else {
      setTitle("");
      setContent("");
      setExcerpt("");
      setCategory("news");
      setAuthor("ekvalleys");
      setImageUrl("");
      setIsFeatured(false);
    }
  }, [post, isOpen]);

  const handleSave = async () => {
    if (!title.trim() || !content.trim() || !excerpt.trim()) {
      toast.error("Title, content, and excerpt are required");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/blog/posts/${post.id}`, {
          title, content, excerpt, category, author,
          image_url: imageUrl || null,
          is_featured: isFeatured,
        }, { headers });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog/posts`, {
          title, content, excerpt, category, author,
          image_url: imageUrl || null,
          is_featured: isFeatured,
        }, { headers });
        toast.success("Post created");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save post");
    }
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#EFEDE5]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#1A1915]">
            {isEdit ? "Edit Post" : "Create New Post"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Title */}
          <div>
            <Label className="text-[#1A1915] font-medium">Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 bg-white border-[#D1CFC5]"
              placeholder="Post title"
              data-testid="blog-title-input"
            />
          </div>

          {/* Category & Author */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#1A1915] font-medium">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1 bg-white border-[#D1CFC5]" data-testid="blog-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="news">Valley News</SelectItem>
                  <SelectItem value="community">Community</SelectItem>
                  <SelectItem value="sponsor">Sponsor Promotions</SelectItem>
                  <SelectItem value="premium">Premium Services</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#1A1915] font-medium">Author</Label>
              <Input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className="mt-1 bg-white border-[#D1CFC5]"
                data-testid="blog-author-input"
              />
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <Label className="text-[#1A1915] font-medium">Excerpt *</Label>
            <Textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              className="mt-1 bg-white border-[#D1CFC5]"
              placeholder="Short summary shown in post list..."
              rows={2}
              data-testid="blog-excerpt-input"
            />
          </div>

          {/* Content - Rich Text Editor */}
          <div>
            <Label className="text-[#1A1915] font-medium">Content *</Label>
            <div className="mt-1 bg-white border border-[#D1CFC5] rounded-lg overflow-hidden" data-testid="blog-content-editor">
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["blockquote", "link", "image"],
                    ["clean"],
                  ],
                }}
                formats={["header", "bold", "italic", "underline", "strike", "list", "blockquote", "link", "image"]}
                placeholder="Write your post content here..."
                style={{ minHeight: "250px" }}
              />
            </div>
          </div>

          {/* Featured Image */}
          <div>
            <Label className="text-[#1A1915] font-medium">Featured Image</Label>
            <div className="mt-1 flex items-center gap-3">
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="bg-white border-[#D1CFC5] flex-1"
                placeholder="Image URL (paste or upload below)"
                data-testid="blog-image-url-input"
              />
              {imageUrl && (
                <button onClick={() => setImageUrl("")} className="text-[#5C5A52] hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {imageUrl && (
              <img src={imageUrl} alt="Preview" className="mt-2 h-32 object-cover rounded-lg border border-[#D1CFC5]" />
            )}
          </div>

          {/* Media Upload */}
          <div className="bg-[#F7F6F2] border border-[#D1CFC5] rounded-lg p-4">
            <Label className="text-[#1A1915] font-medium mb-3 block">Media Library</Label>
            <p className="text-xs text-[#5C5A52] mb-3">Upload files or click an image to use it as the featured image.</p>
            <MediaUploader
              token={token}
              onSelect={(url) => setImageUrl(url)}
            />
          </div>

          {/* Featured Toggle */}
          <label className="flex items-center gap-3 cursor-pointer" data-testid="blog-featured-toggle">
            <input
              type="checkbox"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="w-4 h-4 accent-[#2C4A3B]"
            />
            <span className="text-[#1A1915] font-medium">Mark as Featured Post</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold px-8"
            data-testid="blog-save-btn"
          >
            {saving ? "Saving..." : isEdit ? "Update Post" : "Publish Post"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
